import React, { useMemo } from 'react'
import { Button } from 'antd'
import moment from 'moment'
import { IBestDataset } from '../../../../../../types/analytics'
import { IEnrichedMetadata } from '../../../../../../types/metadata'
import { IAdvancedAnalyticsApiPayload } from '../../../../../../types/chart'
import { IAnalytcisUserInput } from '../../../../../../types/IAnalyticsUserInput'
import { MarkerItem } from '../../../../../../types/home'
import { useStatisticsParams } from "../../../hooks/useStatisticsParams"
import { AnalyticsSection } from "../../Layout/AnalyticsSection"
import StatisticsInputForm from "./StatisticsInputForm"
import StatisticsChart from "./StatisticsChart"

interface StatisticsProps {
  coordinates: number[]
  previewDatasetId: string
  datasets: IBestDataset[]
  dateRange: [moment.Moment, moment.Moment]
  onDatasetChange: (item: string) => void
  enrichedMetadata: IEnrichedMetadata[]
  onUpdate: (payload: IAdvancedAnalyticsApiPayload) => void
  statisticsImages: string
  statisticsValuesLinks: string[]
  error: string
  pointName: string
  updating: boolean
  userInput: IAnalytcisUserInput
  updateUserInput: React.Dispatch<Partial<IAnalytcisUserInput>>
  point?: MarkerItem
  onChangeActiveKey: (item: any) => void
  pointOption: { label: string, value: string }[]
  globalDateRange: any[]
  setGlobalDateRange: (item: any) => void
  isForAllAnalytics: boolean
  setIsForAllAnalytics: (item: any) => void
  isOfflineDataset: boolean
}

export default function Statistics(props: StatisticsProps) {
  const {
    datasets,
    statisticsImages,
    statisticsValuesLinks,
    error,
    updating,
    pointName,
    isOfflineDataset
  } = props

  const {
    listVariablesX,
    dataset,
    variableX,
    startDate,
    endDate,
    categories,
    onUpdateStatistics,
    doVariableChange,
    doStartDateChange,
    doEndDateChange,
    onSelectDataset,
    handleCheckboxChange,
    disabledStartDate,
    disabledEndDate
  } = useStatisticsParams(props)

  const plotInputContent = useMemo(() => (
    <StatisticsInputForm
      datasets={datasets}
      dataset={props.previewDatasetId}
      onSelectDataset={onSelectDataset}
      categories={categories}
      variables={listVariablesX}
      variableX={variableX}
      onVariableChange={doVariableChange}
      startDate={startDate}
      endDate={endDate}
      onStartDateChange={doStartDateChange}
      onEndDateChange={doEndDateChange}
      disabledStartDate={disabledStartDate}
      disabledEndDate={disabledEndDate}
      isForAllAnalytics={props.isForAllAnalytics}
      onCheckboxChange={handleCheckboxChange}
      isLoading={updating}
      hasError={!!error}
      isOfflineDataset={isOfflineDataset}

    />
  ), [
    datasets,
    props.previewDatasetId,
    onSelectDataset,
    categories,
    listVariablesX,
    variableX,
    doVariableChange,
    startDate,
    endDate,
    doStartDateChange,
    doEndDateChange,
    disabledStartDate,
    disabledEndDate,
    props.isForAllAnalytics,
    handleCheckboxChange,
    updating,
    error
  ])

  const updateContent = useMemo(() => (
    <div style={{
      width: '100%',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '36px'
    }}>
      <Button
        type="primary"
        disabled={updating || isOfflineDataset}
        loading={updating}
        onClick={() => onUpdateStatistics()}
        style={{
          width: '90px',
          height: '36px'
        }}
      >
        Update
      </Button>
    </div>
  ), [
    updating,
    onUpdateStatistics
  ])

  const chartContent = useMemo(() => (
    <StatisticsChart
      statisticsImages={statisticsImages}
      statisticsValuesLinks={statisticsValuesLinks}
      pointName={pointName}
      isLoading={updating}
      error={error}
      isOfflineDataset={isOfflineDataset}
    />
  ), [
    statisticsImages,
    statisticsValuesLinks,
    pointName,
    updating,
    error
  ])

  return (
    <AnalyticsSection
      plotInputContent={plotInputContent}
      updateContent={updateContent}
      chartContent={chartContent}
      additionalContent={null}
      isLoading={updating}
    />
  )
}
